<template>
  <Card id="attachments-card">
    <CardHeader class="flex flex-wrap justify-between items-center">
      <CardTitle>Attachments</CardTitle>
      <div class="flex items-center gap-2">
        <LoadingSpinner class="mt-1" />
        <Button variant="card-header-action" size="sm" @click="showEsignature = true">Send E-signature</Button>
      </div>
    </CardHeader>
    <CardContent>
      <HFetchQuery :query="esignatureQuery">
        <template #default="{ data: esignatures }">
          <div v-if="esignatures.length > 0" class="space-y-2 mb-2">
            <div
              v-for="sig of esignatures"
              :key="sig.audit_log_url"
              class="flex items-center gap-3 rounded-lg bg-gray-100 px-3 py-2.5"
            >
              <vue-feather type="feather" class="shrink-0 h-4 w-4 text-gray-400" />
              <span class="flex-1 text-sm truncate">{{ sig.temp_name }}</span>
              <Badge :variant="esigBadgeVariant(esigStatusOverride(sig))">{{ esigStatusOverride(sig) }}</Badge>
              <DropdownMenu v-if="sig.docs.length > 0 || sig.audit_log_url">
                <DropdownMenuTrigger as-child>
                  <button class="shrink-0 text-gray-400 hover:text-gray-600 transition-colors">
                    <vue-feather type="more-horizontal" class="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem v-for="(doc, index) of sig.docs" :key="index" as-child>
                    <a target="_blank" :href="doc.url" class="flex items-center gap-2">
                      <vue-feather type="external-link" class="h-4 w-4" />
                      View Document {{ index + 1 }}
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator v-if="sig.docs.length > 0" />
                  <DropdownMenuItem v-if="sig.audit_log_url" as-child>
                    <a target="_blank" :href="sig.audit_log_url" class="flex items-center gap-2">
                      <vue-feather type="external-link" class="h-4 w-4" />
                      View Audit Log
                    </a>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </template>
      </HFetchQuery>

      <div v-if="files" class="space-y-2">
        <a
          v-for="file of files"
          :key="file.name"
          :href="file.url"
          target="_blank"
          class="flex items-center gap-3 rounded-lg bg-gray-100 px-3 py-2.5 hover:bg-gray-200/70 transition-colors group"
        >
          <vue-feather type="file" class="shrink-0 h-4 w-4 text-gray-400" />
          <span class="flex-1 text-sm truncate">{{ file.name }}</span>
          <button
            class="shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
            @click.prevent.stop="fileToDelete = file.id"
          >
            <vue-feather type="x" class="h-4 w-4" />
          </button>
        </a>
      </div>
      <div class="mt-3">
        <FileInput
          :key="fileInputKey"
          limits="any file"
          :multiple="true"
          :model-value="newFiles"
          accept="*"
          @update:model-value="newFiles = $event"
        />
        <div v-if="newFiles" class="mt-2">
          <Button size="sm" @click="updateContact()">Upload</Button>
        </div>
      </div>
    </CardContent>

    <Dialog :open="!!fileToDelete" @update:open="fileToDelete = null">
      <DialogContent class="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete attachment</DialogTitle>
          <DialogDescription>Are you sure? This action cannot be undone.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose as-child>
            <Button variant="outline" @click="fileToDelete = null">Cancel</Button>
          </DialogClose>
          <Button variant="destructive" @click="confirmDelete">Delete</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <ContactEsignature
      v-if="showEsignature"
      :record="{ id: contactId }"
      @handle-submit="
        showEsignature = false;
        esignatureQuery.reload();
      "
      @handle-cancel="showEsignature = false"
    />
  </Card>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import LoadingSpinner from '../shared/LoadingSpinner.vue';
import { Button } from '@/components/ui/button';
import { Badge, type BadgeVariants } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import ContactEsignature from '@/components/contact/ContactEsignature.vue';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner';
import { Api } from '@/api';
import type { ActiveStorageBlobEntity } from '@/api/entity_types/ActiveStorageBlob';
import type { EsignatureStatus, EsignatureSubmissionEntity } from '@/api/entity_types/EsignatureSubmission';
import { toast } from 'vue-sonner';
import { useHFetchQuery } from '@/lib/hfetch/useHFetchQuery.ts';
import HFetchQuery from '@/lib/hfetch/HFetchQuery.vue';

type AttachmentsProps = {
  contactId: number;
  files: ActiveStorageBlobEntity[] | null;
};
const { contactId, files } = defineProps<AttachmentsProps>();
const loadingSpinnerStore = useLoadingSpinnerStore();

const emit = defineEmits(['reload']);

const newFiles = ref<ActiveStorageBlobEntity[] | null>(null);
const showEsignature = ref(false);
const fileInputKey = ref(0);
const fileToDelete = ref<number | null>(null);

const esignatureQuery = useHFetchQuery(async () => {
  const response = await Api.EsignatureSubmissions.getEsignatureSubmissions({ contactId });
  return { status: 'success', data: response?.records ?? [] };
});

const confirmDelete = async () => {
  if (fileToDelete.value) {
    await handleDelete(fileToDelete.value);
    fileToDelete.value = null;
  }
};

const esigBadgeVariant = (status: EsignatureStatus): BadgeVariants['variant'] => {
  const variants: Record<EsignatureStatus, BadgeVariants['variant']> = {
    completed: 'green',
    opened: 'yellow',
    sent: 'blue',
    declined: 'red',
  };
  return variants[status];
};

const handleDelete = async (id: number) => {
  loadingSpinnerStore.showLocalLoadingSpinner();
  const data = await Api.Attachments.deleteAttachment({ id });
  loadingSpinnerStore.hideLocalLoadingSpinner();

  if (data.status === 'success') {
    emit('reload');
    toast.success('Attachment deleted');
  } else {
    toast.error('Attachment deletion error', {
      description: data.message,
    });
  }
};

const updateContact = async () => {
  if (!newFiles.value) return;
  let formData = new FormData();
  for (const file of newFiles.value) {
    formData.append('files[]', file as unknown as Blob, file.name);
  }
  loadingSpinnerStore.showLocalLoadingSpinner();
  const data = await Api.Contacts.updateContact({ contactId, payload: formData });
  loadingSpinnerStore.hideLocalLoadingSpinner();

  if (data.status === 'success') {
    newFiles.value = null;
    fileInputKey.value++;
    emit('reload');
    toast.success('Attachments uploaded');
  } else {
    toast.error('Attachments upload error', {
      description: data.message,
    });
  }
};

const esigStatusOverride = (sig: EsignatureSubmissionEntity): EsignatureStatus => {
  const isDeclinedPolicy = sig.status === 'completed' && sig.docs.length === 0 && !sig.audit_log_url;
  if (isDeclinedPolicy) return 'declined';

  return sig.status;
};
</script>
